import React from 'react';
import { BookA, Languages, Sparkles, MessageCircle, SpellCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../context/ChatContext';
import { trackEvent } from '../lib/analytics';

// Mode configuration for grouped suggestions with subtitles explaining purpose
const MODE_CONFIG = {
  ask: {
    label: 'Ask',
    subtitle: 'Chat freely about anything',
    icon: MessageCircle,
    color: '#666'
  },
  dict: {
    label: 'Dictionary',
    subtitle: 'Look up words & phrases',
    icon: BookA,
    color: '#3498db'
  },
  grammar: {
    label: 'Grammar',
    subtitle: 'Analyze sentence structures',
    icon: Languages,
    color: '#9b59b6'
  },
  native: {
    label: 'Native',
    subtitle: 'Learn authentic expressions',
    icon: Sparkles,
    color: '#f5a623'
  },
  correction: {
    label: 'Fix',
    subtitle: 'Proofread your writing',
    icon: SpellCheck,
    color: '#FF7675'
  },
};

/**
 * SuggestionButtons component
 * Renders clickable suggestion buttons for follow-up questions
 * Extracted from mellow:suggestions code blocks in AI responses
 *
 * @param {string[]} suggestions - Array of suggestion texts
 * @param {string} mode - Optional mode: 'ask' (default), 'dict', 'grammar', 'native'
 * @param {boolean} showHeader - Whether to show mode header (for welcome state)
 * @param {string} initialMode - Mode when chat was created (for course-specific buttons)
 * @param {number} messageIndex - Index of the message (0-based, used to determine /start vs /continue)
 * @param {boolean} disabled - When true, buttons are visible but not clickable (for shared view)
 */
export default function SuggestionButtons({ suggestions, mode = 'ask', showHeader = false, initialMode = null, messageIndex = 0, disabled = false }) {
  const { t } = useTranslation('chat');
  const { sendMessage, lookupDictionary, lookupGrammar, lookupNative, lookupCorrection, sending, messages } = useChat();

  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  // Helper to track course first action
  const trackCourseAction = (action, buttonText) => {
    // Get course topic from first message (user message with the topic)
    const courseTopic = messages && messages.length > 0 ? messages[0]?.content : '';

    trackEvent('course_first_action', {
      action,
      button_text: buttonText,
      course_topic: courseTopic,
    });
  };

  const handleSuggestionClick = (suggestion) => {
    if (sending) return;

    // Track course first action for suggestion clicks
    if (isCourseChat && isFirstAIMessage) {
      // Detect specific action types
      let action = 'other';
      if (suggestion === '调整课程配置') {
        action = 'config';
      } else if (suggestion === '换个话题重新规划') {
        action = 'change_topic';
      }
      trackCourseAction(action, suggestion);
    }

    // Trigger appropriate action based on mode
    switch (mode) {
      case 'dict':
        lookupDictionary(suggestion);
        break;
      case 'grammar':
        lookupGrammar(suggestion);
        break;
      case 'native':
        lookupNative(suggestion);
        break;
      case 'correction':
        lookupCorrection(suggestion);
        break;
      default:
        sendMessage(suggestion);
    }
  };

  const config = MODE_CONFIG[mode] || MODE_CONFIG.ask;
  const Icon = config.icon;

  // Check if this is a course chat
  const isCourseChat = initialMode === 'course';
  // First AI message (index 1) shows /start, subsequent messages show /continue + /test
  const isFirstAIMessage = messageIndex === 1;

  return (
    <div className={`suggestion-group ${showHeader ? 'with-header' : ''} ${disabled ? 'disabled' : ''}`}>
      {showHeader && (
        <div className="suggestion-header" style={{ '--header-color': config.color }}>
          <div className="suggestion-header-main">
            <Icon size={14} />
            <span>{config.label}</span>
          </div>
          <div className="suggestion-header-subtitle">{config.subtitle}</div>
        </div>
      )}
      <div className="suggestion-buttons">
        {/* Course-specific buttons at the top */}
        {isCourseChat && isFirstAIMessage && (
          // First AI message: show /start button
          <button
            className="suggestion-btn course-start"
            onClick={() => {
              if (!disabled) {
                trackCourseAction('start', '/start');
                sendMessage('/start');
              }
            }}
            disabled={sending || disabled}
            style={{ backgroundColor: '#10b981', color: 'white' }}
          >
            /start {t('course.start')}
          </button>
        )}
        {isCourseChat && !isFirstAIMessage && (
          // Subsequent messages: show /continue and /test buttons
          <>
            <button
              className="suggestion-btn course-continue"
              onClick={() => !disabled && sendMessage('/continue')}
              disabled={sending || disabled}
              style={{ backgroundColor: '#8B5CF6', color: 'white' }}
            >
              /continue {t('course.continue')}
            </button>
            <button
              className="suggestion-btn course-test"
              onClick={() => !disabled && sendMessage('/test')}
              disabled={sending || disabled}
              style={{ backgroundColor: '#3498db', color: 'white' }}
            >
              /test {t('course.test')}
            </button>
          </>
        )}
        {/* Regular suggestions */}
        {suggestions.map((suggestion, index) => (
          <button
            key={index}
            className="suggestion-btn"
            onClick={() => !disabled && handleSuggestionClick(suggestion)}
            disabled={sending || disabled}
          >
            {suggestion}
          </button>
        ))}
      </div>
    </div>
  );
}

